// frontend/src/pages/ExportPage.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  startQrZipExport,
  getQrZipExportStatus,
  startGdprExport,
} from "../api/codes";
import { exportOrgScans } from "../api/analytics";
import { getUsage } from "../api/orgs";

const FREE_PLANS = new Set(["free", "starter"]);
const POLL_INTERVAL_MS = 3000;

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function defaultFromDate() {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return isoDate(d);
}

// --------------------------------------------------------------------------- //
// Section card wrapper                                                          //
// --------------------------------------------------------------------------- //

function SectionCard({ title, description, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="p-5 border-b border-gray-100">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        {description && (
          <p className="mt-1 text-sm text-gray-500">{description}</p>
        )}
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// ExportPage                                                                    //
// --------------------------------------------------------------------------- //

function ExportPage() {
  // Plan
  const [planName, setPlanName] = useState(null);

  // Section 1 — QR ZIP export
  const [qrPhase, setQrPhase] = useState("idle"); // idle | loading | polling | ready | error
  const [qrUrl, setQrUrl] = useState(null);
  const pollTimer = useRef(null);

  // Section 2 — Analytics export
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(() => isoDate(new Date()));
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Section 3 — GDPR export
  const [gdprPhase, setGdprPhase] = useState("idle"); // idle | loading | sent

  // Fetch plan on mount
  useEffect(() => {
    getUsage()
      .then((u) => setPlanName(u?.plan?.name ?? null))
      .catch(() => {});
    return () => clearTimeout(pollTimer.current);
  }, []);

  const isFreePlan =
    planName !== null && FREE_PLANS.has(planName.toLowerCase());

  // ── Section 1: QR ZIP ───────────────────────────────────────────────────── //

  const pollQrStatus = (taskId) => {
    pollTimer.current = setTimeout(async () => {
      try {
        const result = await getQrZipExportStatus(taskId);
        if (result.status === "complete") {
          setQrUrl(result.download_url);
          setQrPhase("ready");
        } else if (result.status === "failed") {
          setQrPhase("error");
          toast.error("QR export failed.");
        } else {
          pollQrStatus(taskId);
        }
      } catch {
        setQrPhase("error");
        toast.error("Failed to check export status.");
      }
    }, POLL_INTERVAL_MS);
  };

  const handleQrExport = async () => {
    setQrPhase("loading");
    setQrUrl(null);
    clearTimeout(pollTimer.current);
    try {
      const { task_id } = await startQrZipExport();
      setQrPhase("polling");
      pollQrStatus(task_id);
    } catch {
      setQrPhase("error");
      toast.error("Failed to start QR export.");
    }
  };

  // ── Section 2: Analytics ─────────────────────────────────────────────────── //

  const handleAnalyticsExport = async () => {
    setAnalyticsLoading(true);
    try {
      await exportOrgScans(fromDate, toDate);
    } catch (err) {
      // 402 already handled by the global plan-limit interceptor
      if (err?.response?.status !== 402) {
        toast.error("Analytics export failed.");
      }
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // ── Section 3: GDPR ──────────────────────────────────────────────────────── //

  const handleGdprExport = async () => {
    setGdprPhase("loading");
    try {
      await startGdprExport();
      setGdprPhase("sent");
    } catch (err) {
      setGdprPhase("idle");
      const detail =
        err?.response?.data?.detail ?? "Failed to request GDPR export.";
      toast.error(detail);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <h1 className="text-xl font-semibold text-gray-900">Export</h1>

      {/* ── Section 1: QR Image Export ───────────────────────────────────── */}
      <SectionCard
        title="QR Image Export"
        description="Download all your active QR codes as PNG images bundled in a ZIP archive with a manifest."
      >
        {qrPhase === "idle" || qrPhase === "error" ? (
          <button
            onClick={handleQrExport}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            Export all QR codes as PNG ZIP
          </button>
        ) : qrPhase === "loading" || qrPhase === "polling" ? (
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <svg
              className="w-5 h-5 animate-spin text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            <span>Export in progress… checking every 3 seconds.</span>
          </div>
        ) : (
          /* ready */
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 text-sm text-green-700">
              <svg
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="font-medium">Download Ready</span>
            </div>
            <div className="flex gap-3">
              <a
                href={qrUrl}
                target="_blank"
                rel="noreferrer"
                className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors"
              >
                ↓ Download ZIP
              </a>
              <button
                onClick={handleQrExport}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Export again
              </button>
            </div>
          </div>
        )}
        {qrPhase === "error" && (
          <p className="text-sm text-red-600">Export failed. Please try again.</p>
        )}
      </SectionCard>

      {/* ── Section 2: Analytics Export ──────────────────────────────────── */}
      <SectionCard
        title="Analytics Export"
        description="Download scan data as CSV for a date range."
      >
        {isFreePlan ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <span className="text-lg leading-none">🔒</span>
            <span>
              Analytics export requires Pro or higher.{" "}
              <Link to="/billing" className="text-blue-600 font-medium underline">
                Upgrade plan
              </Link>
            </span>
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label
                className="block text-xs font-medium text-gray-600 mb-1"
                htmlFor="export-from"
              >
                From
              </label>
              <input
                id="export-from"
                type="date"
                value={fromDate}
                max={toDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label
                className="block text-xs font-medium text-gray-600 mb-1"
                htmlFor="export-to"
              >
                To
              </label>
              <input
                id="export-to"
                type="date"
                value={toDate}
                min={fromDate}
                max={isoDate(new Date())}
                onChange={(e) => setToDate(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={handleAnalyticsExport}
              disabled={analyticsLoading}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {analyticsLoading ? "Exporting…" : "Export Scan Data (CSV)"}
            </button>
          </div>
        )}
      </SectionCard>

      {/* ── Section 3: GDPR Export ────────────────────────────────────────── */}
      <SectionCard
        title="GDPR Data Export"
        description="Export all of your organisation's data in a machine-readable format."
      >
        <div className="text-sm text-gray-600 space-y-1">
          <p>The export ZIP includes:</p>
          <ul className="list-disc list-inside space-y-0.5 text-gray-500">
            <li>Your user profile</li>
            <li>Organisation details and plan</li>
            <li>All QR codes (including deleted)</li>
            <li>All scan events</li>
            <li>Team memberships</li>
            <li>Audit log</li>
          </ul>
          <p className="pt-1">
            A download link will be emailed to you when the export is ready
            (valid for 24 hours).
          </p>
        </div>

        {gdprPhase === "sent" ? (
          <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            <svg
              className="w-5 h-5 shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>
              Export started. You will receive an email when your data is ready.
            </span>
          </div>
        ) : (
          <button
            onClick={handleGdprExport}
            disabled={gdprPhase === "loading"}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {gdprPhase === "loading"
              ? "Requesting export…"
              : "Export all my organisation's data"}
          </button>
        )}
      </SectionCard>
    </div>
  );
}

export default ExportPage;
